import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { SEO } from '../components/SEO';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'tools', label: 'Digital Tools — Internal R&D' },
  { key: 'studies', label: 'Representative Case Studies' },
];

const DIGITAL_TOOLS = [
  {
    title: 'Mass Concrete Temperature Predictor',
    tag: 'Thermal Engineering',
    category: 'tools',
    image: 'https://images.unsplash.com/photo-1673978484091-6a743a9058cf?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    path: '/engineering/calculator/thermal',
    desc: 'A finite-difference heat solver for mass concrete pours. Predicts peak core temperature, differential gradients, and time-to-peak for foundations and pile caps. Incorporates cement hydration kinetics for CEM I, CEM I + fly ash, and CEM I + GGBS blends, with real-time cold weather assessment against ACI 306R / BS EN 206 thresholds.',
    attrs: ['Adiabatic heat rise modelling by cement type', 'Sub-zero ambient support (down to -40\u00b0C)', 'City-specific climate data via Open-Meteo', 'Automated cold weather concreting risk flag'],
  },
  {
    title: 'Service Life Estimator (XC3 Carbonation)',
    tag: 'Durability Engineering',
    category: 'tools',
    image: 'https://customer-assets.emergentagent.com/job_0a2be3ae-68ce-414a-b1fb-5ae16a7a4416/artifacts/pyjxgm5u_betonschaeden-hero.jpg',
    path: '/engineering/calculator/carbonation',
    desc: 'Probabilistic carbonation depth prediction based on the fib Model Code 34 (FORM method). Estimates the service life of reinforced concrete exposed to atmospheric CO\u2082, accounting for local relative humidity, CO\u2082 concentration, and the carbonation resistance of the binder system.',
    attrs: ['Monte Carlo simulation with 95th percentile confidence intervals', 'RH-dependent reliability index (\u03b2 \u2265 0.5 dry / \u03b2 \u2265 1.28 humid)', 'Sustainability comparison against lower-carbon cement alternatives', 'Automatic RACC,0 derivation from w/c ratio and cement type'],
  },
  {
    title: 'Identity Testing Checker',
    tag: 'Quality Assurance',
    category: 'tools',
    image: 'https://images.unsplash.com/photo-1624804523111-d79f713350f9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    path: '/engineering/identity-testing',
    desc: 'Automated compliance checking for concrete cube identity testing under BS EN 206 and BS 8500. Evaluates whether a batch of test results meets the Shewhart mean and range criteria for a given strength class, replacing manual spreadsheet checks with instant pass/fail assessment.',
    attrs: ['Shewhart mean and range rule evaluation', 'BS EN 206 / BS 8500 compliance logic', 'Batch input with instant pass/fail output', 'Exportable results for quality records'],
  },
];

const CASE_STUDIES = [
  {
    title: 'Thermal Control Plan — Mass Foundation',
    tag: 'Thermal Engineering',
    category: 'studies',
    image: 'https://images.unsplash.com/photo-1710585761854-57ff6839395c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    desc: '3D finite element thermal analysis and companion spreadsheet modelling for a 3.2m deep raft foundation. Peak core temperature, differential gradients, and insulation schedules were derived using adiabatic heat rise inputs calibrated to the specified CEM III/B binder. The study informed pour sequencing, cooling pipe layout, and minimum striking times to limit early-age thermal cracking risk.',
  },
  {
    title: 'Durability Assessment — Sewerage Tunnel Infrastructure',
    tag: 'Durability Engineering',
    category: 'studies',
    image: 'https://customer-assets.emergentagent.com/job_0a2be3ae-68ce-414a-b1fb-5ae16a7a4416/artifacts/krg0k0zq_103974075_8db3682a39.jpg',
    desc: 'Probabilistic carbonation and chloride ingress modelling for a large-diameter segmental tunnel lining in the Middle East. Cover depth, w/c ratio, and binder system were optimised against a 120-year design life under combined XC3/XC4 and XS2 exposure classes, with sensitivity analysis across three cement replacement scenarios and region-specific CO\u2082 / temperature profiles.',
  },
  {
    title: 'Concrete Materials Specification — Nuclear Infrastructure',
    tag: 'Materials Engineering',
    category: 'studies',
    image: 'https://images.unsplash.com/photo-1692340797397-7c902f8173be?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600',
    desc: 'Development of performance-based concrete specifications for intermediate-level waste (ILW) storage enclosures. Mix design constraints addressed low heat of hydration, sulfate resistance, and long-term dimensional stability, with compliance mapped against BS 8500, NDA guidance, and site-specific durability acceptance criteria for a 500-year containment period.',
  },
];

const ALL_ITEMS = [...DIGITAL_TOOLS, ...CASE_STUDIES];

export const Projects = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? ALL_ITEMS : ALL_ITEMS.filter(i => i.category === filter);

  return (
    <div className="min-h-screen pt-16" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <SEO title={`${t('projects.title')} | Durable Designs`} description={t('projects.subtitle')} path="/engineering/projects" />

      {/* Hero */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ background: '#06120B' }}>
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tighter mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="projects-heading">
            {t('projects.title')}
          </h1>
          <p className="text-base text-neutral-400 max-w-2xl mb-6">{t('projects.subtitle')}</p>
          <p className="text-sm text-neutral-500 max-w-2xl leading-relaxed">
            Our proprietary digital tools are developed in-house as a long-term R&D investment — applying materials science and structural engineering principles to automated, repeatable workflows. Client project documentation will be published here as engagements are delivered.
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <div className="max-w-7xl mx-auto flex flex-wrap gap-2" data-testid="eng-projects-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className="px-4 py-2 text-[10px] font-semibold tracking-[0.15em] uppercase transition-colors"
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                background: filter === tab.key ? '#059669' : 'transparent',
                color: filter === tab.key ? '#fff' : '#6b7280',
                border: `1px solid ${filter === tab.key ? '#059669' : '#e5e7eb'}`,
              }}
              data-testid={`eng-projects-tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {/* Items */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {filtered.map((item, index) => (
              <div key={`${item.title}-${filter}`} className="border border-gray-200 group overflow-hidden flex flex-col" data-testid={`project-card-${index}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="text-xs font-semibold tracking-[0.15em] uppercase text-emerald-600 mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {item.tag} {item.category === 'tools' ? '— Live Tool' : '— Case Study'}
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2 tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed mb-4">{item.desc}</p>

                  {item.attrs && (
                    <ul className="mb-4 space-y-1">
                      {item.attrs.map((attr, i) => (
                        <li key={i} className="text-xs text-gray-400 leading-relaxed flex items-start gap-2">
                          <span className="text-emerald-500 mt-0.5 flex-shrink-0">&#x2022;</span>{attr}
                        </li>
                      ))}
                    </ul>
                  )}

                  {item.path && (
                    <Link to={item.path} className="mt-auto inline-flex items-center gap-2 text-xs font-semibold text-emerald-600 hover:text-emerald-700 transition-colors" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid={`project-link-${index}`}>
                      Launch Tool <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
